import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { ConnectionIcon } from 'app/common/presentation'
import { ArrowUpRightIcon } from 'app/common/presentation/icons'
import { mapToUrl } from 'app/connections/presentation/connectionUrlMapper'
import { AvailableSignalPlatform } from 'app/signals/types'

import { Button, Chip, CodeTag } from 'components'

import styles from './CreateSignal.module.scss'

interface PlatformCardProps {
  connection: AvailableSignalPlatform
  filter: 'all' | 'ready' | 'needsData'
}

const PlatformCard = ({ connection, filter }: PlatformCardProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation('signals')
  const { t: tConnections } = useTranslation('connections')
  const groups = [
    { type: 'audience', options: connection.audiences },
    { type: 'conversion', options: connection.conversions },
  ]
  const options = groups.flatMap((group) => group.options)
  const visibleGroups = groups
    .map((group) => ({
      ...group,
      options: group.options.filter(
        ({ valid }) => filter === 'all' || (filter === 'ready' ? valid : !valid),
      ),
    }))
    .filter((group) => group.options.length > 0)

  if (filter !== 'all' && visibleGroups.length === 0) return null

  return (
    <section className={styles.platformCard}>
      <header className={styles.platformHeader}>
        <div className={styles.platformInfo}>
          <ConnectionIcon connection={connection} style={{ height: 20, width: 20 }} />
          <h2>{tConnections(`available.${connection.name}.displayName`)}</h2>
          <Chip color="green">{t('create.connected')}</Chip>
        </div>
        <span className={styles.availability}>
          {t('create.available', {
            available: options.filter(({ valid }) => valid).length,
            total: options.length,
          })}
        </span>
      </header>
      <div className={styles.signalOptions}>
        {visibleGroups.map(({ type, options }) => (
          <div key={type}>
            <h3 className={styles.groupLabel}>{t(`create.group.${type}`)}</h3>
            {options.map((option) => (
              <div className={styles.signalOption} key={option.name}>
                <div className={styles.signalContent}>
                  <div className={styles.signalInfo}>
                    <strong>
                      {t([
                        `create.platformPurpose.${connection.name}.${option.name}`,
                        `create.purpose.${option.name}`,
                      ])}
                    </strong>
                    <span>{t(`create.description.${option.name}`)}</span>
                  </div>
                  {!option.valid && option.needs && (
                    <div className={styles.requirement}>
                      <span>{t('create.needs')}</span>
                      {option.needs.properties.map((property, index) => (
                        <Fragment key={property}>
                          {index > 0 && <span>{t('create.or')}</span>}
                          <CodeTag color="grey">{property}</CodeTag>
                        </Fragment>
                      ))}
                      <span>
                        {t('create.requirement', { call: option.needs.in })}
                        {option.needs.of &&
                          ` ${t('create.requirementOf', { events: option.needs.of })}`}
                      </span>
                    </div>
                  )}
                </div>
                {!option.valid && option.needs ? (
                  <Button
                    rightIcon={<ArrowUpRightIcon />}
                    variant="outlined"
                    onClick={() => {
                      if (!option.needs) return
                      window.open(
                        t(`create.spec.${option.needs.in}.url`),
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }}
                  >
                    {t(`create.spec.${option.needs.in}.label`)}
                  </Button>
                ) : (
                  <Button
                    disabled={!option.valid}
                    small
                    onClick={() =>
                      void navigate(
                        `/signals/new/${mapToUrl(connection.name)}/${type}${type === 'audience' ? `/${option.name}` : ''}`,
                      )
                    }
                  >
                    {t('create.action.create')}
                  </Button>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  )
}

export default PlatformCard
